import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { RefreshCw, TrendingUp, AlertTriangle, CheckCircle2, XCircle, Loader2, Activity, Info, ExternalLink, ArrowDown, Palette } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_ICONS = {
  ai_analysis: Activity,
  scorecard: TrendingUp,
  capability_matrix: CheckCircle2,
  rtm: AlertTriangle,
  self_score: Palette,
};

const SOURCE_COLORS = {
  ai_analysis: { bar: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  scorecard: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  capability_matrix: { bar: 'bg-cyan-500', text: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200' },
  rtm: { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  self_score: { bar: 'bg-violet-500', text: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200' },
};

// Each pending source carries an action: either `scroll` (in-page) or `navigate` (separate route)
const MISSING_SOURCES = {
  ai_analysis: {
    label: 'AI Deep Analysis',
    hint: 'Run AI Analysis in the Ingestion Hub',
    actionType: 'navigate',
    routeSegment: 'ingestion',
    actionLabel: 'Open Ingestion Hub',
  },
  scorecard: {
    label: 'Bid Pursuit Scorecard',
    hint: 'Complete the 35-question scorecard above',
    actionType: 'scroll',
    selector: '[data-testid="scorecard-section"]',
    actionLabel: 'Jump to Scorecard',
  },
  capability_matrix: {
    label: 'Capability Matrix',
    hint: 'Map LCATs in Gap Analysis',
    actionType: 'navigate',
    routeSegment: 'gaps',
    actionLabel: 'Open Gap Analysis',
  },
  rtm: {
    label: 'Compliance Matrix',
    hint: 'Map requirements in Compliance Tracker',
    actionType: 'navigate',
    routeSegment: 'compliance',
    actionLabel: 'Open Compliance Tracker',
  },
  self_score: {
    label: 'Self-Score Coverage',
    hint: 'Score requirements with the 0-3 rubric in Opportunity Intake',
    actionType: 'navigate',
    routeSegment: 'intake',
    actionLabel: 'Open Opportunity Intake',
  },
};

// Per-source methodology shown in the ContributorBar hover tooltip
const SOURCE_METHODOLOGY = {
  ai_analysis: {
    label: 'AI Deep Analysis',
    defaultWeight: 40,
    description: 'Derived from Fit Score v3 — a weighted blend of Capability, Coverage, Experience, and Addressability against the uploaded documents.',
  },
  scorecard: {
    label: 'Go/No-Go Scorecard',
    defaultWeight: 25,
    description: 'Aggregate of the 35-question Bid Pursuit Scorecard. Each section (Prospect / Internal / Market) drives P(Go), P(Win), and Strategy signals.',
  },
  capability_matrix: {
    label: 'Capability Matrix',
    defaultWeight: 20,
    description: 'Computed from LCAT mapping coverage in the Gap Analysis tool. Higher = fewer unmapped labor categories.',
  },
  rtm: {
    label: 'Compliance Matrix',
    defaultWeight: 15,
    description: 'Compliance coverage from the Compliance Matrix. Higher = fewer non-compliant / unmapped SOW requirements.',
  },
  self_score: {
    label: 'Self-Score Coverage',
    defaultWeight: 10,
    description: 'Federal 0-3 self-scoring rubric across extracted requirements. 50% scored coverage + 50% strong-fit (≥2 = Green/Blue) ratio. Bottom-up SME walk of the SOW.',
  },
};

function ContributorBar({ contributor }) {
  const colors = SOURCE_COLORS[contributor.source] || SOURCE_COLORS.ai_analysis;
  const Icon = SOURCE_ICONS[contributor.source] || Activity;
  const pct = Math.round((contributor.normalised_weight || 0) * 100);
  const methodology = SOURCE_METHODOLOGY[contributor.source];

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg border cursor-help transition-colors',
              colors.bg, colors.border, 'hover:shadow-sm'
            )}
            data-testid={`pwin-contributor-${contributor.source}`}
          >
            <Icon className={cn('w-4 h-4 shrink-0', colors.text)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-700">{contributor.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">{pct}% weight</span>
                  <span className={cn('text-sm font-bold', colors.text)}>{Math.round(contributor.score)}%</span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all duration-500', colors.bar)}
                  style={{ width: `${Math.min(100, contributor.score)}%` }}
                />
              </div>
              {/* Detail badges — bumped from 8px to 10px for legibility */}
              {contributor.detail && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {contributor.detail.deal_killers > 0 && (
                    <Badge className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0">
                      {contributor.detail.deal_killers} Deal Killer{contributor.detail.deal_killers > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {contributor.detail.unknowns > 0 && (
                    <Badge className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0">
                      {contributor.detail.unknowns} Unknown{contributor.detail.unknowns > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {contributor.detail.high_gaps > 0 && (
                    <Badge className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0">
                      {contributor.detail.high_gaps} High Gap{contributor.detail.high_gaps > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {contributor.detail.non_compliant > 0 && (
                    <Badge className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0">
                      {contributor.detail.non_compliant} Non-Compliant
                    </Badge>
                  )}
                  {contributor.detail.sow_restates > 0 && (
                    <Badge className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0">
                      {contributor.detail.sow_restates} SOW Flag{contributor.detail.sow_restates > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </TooltipTrigger>
        {methodology && (
          <TooltipContent side="left" className="max-w-[300px]">
            <div className="text-xs space-y-1.5">
              <p className="font-semibold">{methodology.label}</p>
              <p>{methodology.description}</p>
              <p className="text-slate-400">
                Default weight: <strong>{methodology.defaultWeight}%</strong> · Current: <strong>{pct}%</strong>
              </p>
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export default function PwinFeedbackLoop({ pursuitId, token, workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();

  const fetchComposite = useCallback(async () => {
    if (!pursuitId || !token) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/pwin-composite`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        const detail = await res.json().catch(() => ({}));
        setFetchError(detail?.detail || `Could not load pWin signals (HTTP ${res.status})`);
      }
    } catch (err) {
      setFetchError(`Network error while loading pWin signals: ${err?.message || 'unknown'}`);
    }
    setLoading(false);
  }, [pursuitId, token]);

  useEffect(() => { fetchComposite(); }, [fetchComposite]);

  // Auto-refresh when any upstream signal updates on this pursuit.
  // Other components (Scorecard, Capability Matrix, RTM) dispatch
  // `pwin-signal-updated` on window after a successful save.
  useEffect(() => {
    const handler = (evt) => {
      // Only refresh for events targeting this pursuit
      if (evt?.detail?.pursuitId && evt.detail.pursuitId !== pursuitId) return;
      fetchComposite();
    };
    window.addEventListener('pwin-signal-updated', handler);
    return () => window.removeEventListener('pwin-signal-updated', handler);
  }, [pursuitId, fetchComposite]);

  const handlePendingAction = useCallback((source) => {
    const cfg = MISSING_SOURCES[source];
    if (!cfg) return;
    if (cfg.actionType === 'scroll' && cfg.selector) {
      const el = document.querySelector(cfg.selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    if (cfg.actionType === 'navigate' && cfg.routeSegment && workspaceId && pursuitId) {
      navigate(`/${workspaceId}/pursuit/${pursuitId}/${cfg.routeSegment}`);
    }
  }, [navigate, pursuitId, workspaceId]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/pwin-composite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
        toast.success(`Composite pWin recalculated: ${result.composite_pwin ?? 'N/A'}%`);
      } else toast.error('Recalculation failed');
    } catch { toast.error('Network error'); }
    setRecalculating(false);
  };

  const composite = data?.composite_pwin;
  const contributors = data?.contributors || [];
  const signalsAvail = data?.signals_available ?? contributors.length ?? 0;
  // Use backend-provided total when present, otherwise derive from the known sources map
  const signalsTotal = data?.signals_total ?? Object.keys(MISSING_SOURCES).length;
  const lastRecalcRaw = data?.calculated_at || data?.updated_at || null;

  // Derived label for the timestamp — relative for recency, absolute in tooltip
  const lastRecalcRelative = (() => {
    if (!lastRecalcRaw) return null;
    const ts = new Date(lastRecalcRaw);
    if (isNaN(ts.getTime())) return null;
    const diffMs = Date.now() - ts.getTime();
    const diffSec = Math.round(diffMs / 1000);
    if (diffSec < 10) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  })();

  // Determine missing sources
  const activeSourceKeys = new Set(contributors.map(c => c.source));
  const missingSources = Object.entries(MISSING_SOURCES).filter(([key]) => !activeSourceKeys.has(key));

  const scoreColor = composite >= 75 ? 'text-emerald-600' : composite >= 60 ? 'text-amber-600' : composite !== null ? 'text-red-600' : 'text-slate-400';
  const ringColor = composite >= 75 ? '#059669' : composite >= 60 ? '#d97706' : composite !== null ? '#dc2626' : '#94a3b8';

  return (
    <Card className="border-slate-300 shadow-md" data-testid="pwin-feedback-loop">
      <CardHeader className="border-b border-slate-200 bg-slate-900 rounded-t-lg py-3 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <CardTitle className="text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              pWin Signal Tracker
            </CardTitle>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="About pWin Signal Tracker"
                    className="text-slate-400 hover:text-white transition-colors"
                    data-testid="pwin-help-icon"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[320px]">
                  <div className="text-xs space-y-1.5">
                    <p className="font-semibold">Composite pWin Methodology</p>
                    <p>A weighted blend of up to 4 independent signals:</p>
                    <ul className="space-y-0.5 ml-3 list-disc">
                      <li><strong>AI Deep Analysis</strong> — default 40%</li>
                      <li><strong>Go/No-Go Scorecard</strong> — default 25%</li>
                      <li><strong>Capability Matrix</strong> — default 20%</li>
                      <li><strong>Compliance Matrix</strong> — default 15%</li>
                      <li><strong>Self-Score Coverage</strong> — default 10%</li>
                    </ul>
                    <p>Missing signals are excluded and weights are renormalized across active signals only.</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">
              {signalsAvail}/{signalsTotal} Signals
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Score shown in the mini ring below — deduped from header to avoid double display */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="pwin-recalculate-btn"
                    size="sm" variant="ghost"
                    onClick={handleRecalculate}
                    disabled={recalculating}
                    aria-label="Recalculate pWin from all signals"
                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                  >
                    {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Recalculate from all signals</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : fetchError ? (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="pwin-error-banner">
            <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Could not load pWin signals</p>
              <p className="text-xs text-red-700 mt-0.5">{fetchError}</p>
              <button
                type="button"
                onClick={fetchComposite}
                className="text-xs text-red-600 hover:text-red-800 font-medium underline mt-1"
                data-testid="pwin-retry-btn"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Single-signal reliability warning — composite becomes trustworthy only with ≥2 signals */}
            {composite !== null && composite !== undefined && signalsAvail >= 1 && signalsAvail < 2 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="pwin-single-signal-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Single-signal score — not yet a true composite
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Only {signalsAvail} of {signalsTotal} signals is active, so the score reflects one data point renormalized to 100%.
                    Complete at least one more signal to get a reliable weighted composite.
                  </p>
                </div>
              </div>
            )}

            {/* Composite Ring + Score */}
            <div className="flex items-center gap-6 mb-5">
              {/* Mini ring */}
              <div className="relative w-20 h-20 shrink-0" data-testid="pwin-composite-ring">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke={ringColor} strokeWidth="2.5"
                    strokeDasharray={`${(composite || 0)} ${100 - (composite || 0)}`}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={cn('text-lg font-bold', scoreColor)} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {composite !== null && composite !== undefined ? `${composite}%` : '--'}
                  </span>
                </div>
              </div>

              {/* Summary text — reflects signal coverage, not just composite score */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {composite === null || composite === undefined ? 'No signals available yet' :
                   signalsAvail < 2 ? 'Provisional score — add more signals for a reliable read' :
                   composite >= 75 ? 'Strong pursuit — GO indicators across signals' :
                   composite >= 60 ? 'Mixed signals — review gaps before committing' :
                   'Significant gaps detected — consider NO-GO or remediation'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {signalsAvail} of {signalsTotal} evaluation signals active
                  {missingSources.length > 0 && ` — ${missingSources.length} pending`}
                </p>
                {lastRecalcRelative && (
                  <p
                    className="text-[11px] text-slate-400 mt-0.5"
                    title={new Date(lastRecalcRaw).toLocaleString()}
                    data-testid="pwin-last-recalc"
                  >
                    Recalculated {lastRecalcRelative}
                  </p>
                )}
              </div>
            </div>

            {/* Contributors */}
            {contributors.length > 0 && (
              <div className="space-y-2 mb-4" data-testid="pwin-contributors">
                {contributors.map(c => (
                  <ContributorBar key={c.source} contributor={c} />
                ))}
              </div>
            )}

            {/* Missing signals — each row is a clickable action that jumps to the right place */}
            {missingSources.length > 0 && (
              <div className="border border-dashed border-slate-300 rounded-lg p-3" data-testid="pwin-missing-signals">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Pending Signals</p>
                <div className="space-y-1.5">
                  {missingSources.map(([key, info]) => {
                    const Icon = SOURCE_ICONS[key] || Activity;
                    const ActionIcon = info.actionType === 'navigate' ? ExternalLink : ArrowDown;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handlePendingAction(key)}
                        className="group w-full flex items-center gap-2 text-xs text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 rounded px-2 py-1.5 -mx-1 transition-colors text-left"
                        data-testid={`pwin-missing-${key}`}
                        title={info.actionLabel}
                      >
                        <Icon className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 shrink-0" />
                        <span className="font-medium">{info.label}</span>
                        <span className="text-slate-400 group-hover:text-indigo-600 truncate">— {info.hint}</span>
                        <ActionIcon className="w-3 h-3 ml-auto text-slate-300 group-hover:text-indigo-500 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
